"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useDelegationStore } from "@/lib/delegation-store";
import {
  useMedications,
  useCreateMedication,
  useUpdateMedication,
  useDeleteMedication,
} from "@/hooks/use-medications";
import { useSuggestions } from "@/hooks/use-suggestions";
import { Button } from "@/components/ui/button";
import { CalendarDatePicker } from "@/components/ui/calendar-date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  getMedicationDisplayName,
  getMedicationSourcePayload,
  type Medication,
  type MedicationStatus,
} from "@/types/medication";
import type { Suggestion } from "@/types/condition";

const STATUS_BADGE: Record<
  MedicationStatus,
  { key: string; variant: "default" | "secondary" | "outline" }
> = {
  active: { key: "statusActive", variant: "default" },
  paused: { key: "statusPaused", variant: "outline" },
  discontinued: { key: "statusDiscontinued", variant: "secondary" },
};

const STATUS_ORDER: MedicationStatus[] = ["active", "paused", "discontinued"];

type MedicationSuggestion = Suggestion & { _source: "product" | "ingredient" };

function toDateInput(iso: string | null) {
  return iso ? iso.slice(0, 10) : "";
}

function toISODate(dateStr: string) {
  return dateStr ? `${dateStr}T00:00:00Z` : undefined;
}

function getSuggestionLabelKey(source: MedicationSuggestion["_source"]) {
  return source === "product" ? "sourceProduct" : "sourceIngredient";
}

function buildCreateRequest(selection: MedicationSuggestion) {
  if (selection._source === "product" && selection.productId) {
    return {
      productId: selection.productId,
      status: "active" as const,
    };
  }

  const canonicalIngredient = selection.canonicalQuery.trim();
  const selectedLabel = selection.label.trim();
  const shouldPreserveDisplayName =
    selectedLabel.length > 0 &&
    selectedLabel.localeCompare(canonicalIngredient, undefined, {
      sensitivity: "accent",
    }) !== 0;

  return {
    ...(shouldPreserveDisplayName ? { displayName: selectedLabel } : {}),
    activeIngredients: [canonicalIngredient],
    status: "active" as const,
  };
}

// ─── Add Medication Form ──────────────────────────────────────

function AddMedicationForm() {
  const t = useTranslations("medications");
  const tc = useTranslations("common");
  const [name, setName] = useState("");
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<MedicationSuggestion | null>(null);
  const [debouncedName, setDebouncedName] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: productData } = useSuggestions(debouncedName, "product");
  const { data: ingredientData } = useSuggestions(debouncedName, "ingredient");
  const createMedication = useCreateMedication();

  const suggestions = useMemo(() => {
    const products = (productData?.data ?? []).map((s) => ({
      ...s,
      _source: "product" as const,
    }));
    const ingredients = (ingredientData?.data ?? []).map((s) => ({
      ...s,
      _source: "ingredient" as const,
    }));
    return [...products, ...ingredients];
  }, [productData, ingredientData]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(name), 300);
    return () => clearTimeout(timer);
  }, [name]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(s: MedicationSuggestion) {
    setName(s.label);
    setSelectedSuggestion(s);
    setShowSuggestions(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedSuggestion) return;

    createMedication.mutate(
      buildCreateRequest(selectedSuggestion),
      {
        onSuccess: () => {
          setName("");
          setSelectedSuggestion(null);
        },
      }
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative" ref={wrapperRef}>
        <Label htmlFor="med-name" className="mb-2 block">
          {t("medicationName")}
        </Label>
        <Input
          id="med-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setSelectedSuggestion(null);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder={t("medicationPlaceholder")}
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-popover p-1 shadow-md">
            {suggestions.map((s, i) => (
              <li key={`${s.canonicalQuery}-${s._source}-${i}`}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => handleSelect(s)}
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{s.label}</span>
                    {s.secondaryLabel && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {s.secondaryLabel}
                      </span>
                    )}
                  </div>
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">
                    {t(getSuggestionLabelKey(s._source))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="min-h-5 text-xs text-muted-foreground">
          {selectedSuggestion ? (
            <span>
              {t("selectedSuggestion")}: {t(getSuggestionLabelKey(selectedSuggestion._source))}
            </span>
          ) : (
            name.trim().length > 0 && (
              <span>{t("selectionRequired")}</span>
            )
          )}
        </div>
        <Button
          type="submit"
          disabled={!selectedSuggestion || createMedication.isPending}
        >
          {createMedication.isPending ? tc("adding") : tc("add")}
        </Button>
      </div>
    </form>
  );
}

// ─── Edit Dialog ──────────────────────────────────────────────

function EditDialog({
  medication,
  open,
  onOpenChange,
}: {
  medication: Medication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("medications");
  const tc = useTranslations("common");
  const [status, setStatus] = useState<MedicationStatus>(medication.status);
  const [dosage, setDosage] = useState(medication.dosage ?? "");
  const [startedAt, setStartedAt] = useState(toDateInput(medication.startedAt));
  const [stoppedAt, setStoppedAt] = useState(toDateInput(medication.stoppedAt));
  const [notes, setNotes] = useState(medication.notes ?? "");
  const updateMedication = useUpdateMedication();
  const medicationName = getMedicationDisplayName(medication);
  const sourceLabel = medication.productId ? t("sourceProduct") : t("sourceIngredient");

  function resetForm() {
    setStatus(medication.status);
    setDosage(medication.dosage ?? "");
    setStartedAt(toDateInput(medication.startedAt));
    setStoppedAt(toDateInput(medication.stoppedAt));
    setNotes(medication.notes ?? "");
  }

  function handleSave() {
    updateMedication.mutate(
      {
        id: medication.id,
        data: {
          ...getMedicationSourcePayload(medication),
          status,
          dosage: dosage || undefined,
          startedAt: toISODate(startedAt),
          stoppedAt: toISODate(stoppedAt),
          notes: notes || undefined,
        },
      },
      { onSuccess: () => onOpenChange(false) }
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) resetForm();
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("editMedication")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("selectedMedication")}</Label>
            <div className="rounded-lg border bg-muted/20 px-3 py-3">
              <div className="flex items-center gap-2">
                <span className="font-medium">{medicationName}</span>
                <Badge variant="outline">{sourceLabel}</Badge>
              </div>
              {medication.resolvedInns.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {medication.resolvedInns.map((inn) => (
                    <Badge key={inn} variant="secondary" className="font-normal">
                      {inn}
                    </Badge>
                  ))}
                </div>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                {t("sourceLockedHint")}
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{tc("status")}</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as MedicationStatus)}
              >
                <SelectTrigger className="w-full">
                  {/* Render the localized label explicitly: Base UI's SelectValue
                      falls back to the raw value until the popup mounts its items. */}
                  <SelectValue>
                    {t(`status${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("statusActive")}</SelectItem>
                  <SelectItem value="paused">{t("statusPaused")}</SelectItem>
                  <SelectItem value="discontinued">{t("statusDiscontinued")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-dosage">{t("dosage")}</Label>
              <Input
                id="edit-dosage"
                value={dosage}
                onChange={(e) => setDosage(e.target.value)}
                placeholder={t("dosagePlaceholder")}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t("started")}</Label>
              <CalendarDatePicker
                value={startedAt || undefined}
                onChange={setStartedAt}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("stopped")}</Label>
              <CalendarDatePicker
                value={stoppedAt || undefined}
                onChange={setStoppedAt}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-med-notes">{tc("notes")}</Label>
            <Textarea
              id="edit-med-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={tc("notesPlaceholder")}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            {tc("cancel")}
          </DialogClose>
          <Button
            onClick={handleSave}
            disabled={updateMedication.isPending}
          >
            {updateMedication.isPending ? tc("saving") : tc("save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Dialog ────────────────────────────────────────────

function DeleteDialog({
  medication,
  open,
  onOpenChange,
}: {
  medication: Medication;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useTranslations("medications");
  const tc = useTranslations("common");
  const deleteMedication = useDeleteMedication();
  const medicationName = getMedicationDisplayName(medication);

  function handleConfirm() {
    deleteMedication.mutate(medication.id, {
      onSuccess: () => onOpenChange(false),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("deleteMedication")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("deleteConfirm", { name: medicationName })}{" "}
          {tc("cannotBeUndone")}
        </p>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            {tc("cancel")}
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteMedication.isPending}
          >
            {deleteMedication.isPending ? tc("deleting") : tc("delete")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Medication Row ───────────────────────────────────────────

function MedicationRow({ medication }: { medication: Medication }) {
  const t = useTranslations("medications");
  const tc = useTranslations("common");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const badge = STATUS_BADGE[medication.status];
  const medicationName = getMedicationDisplayName(medication);
  const sourceLabel = medication.productId ? t("sourceProduct") : t("sourceIngredient");

  return (
    <>
      <div className="space-y-2 rounded-lg border p-4">
        <p className="font-medium">{medicationName}</p>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={badge.variant}>{t(badge.key)}</Badge>
          <Badge variant="outline">{sourceLabel}</Badge>
        </div>
        {medication.resolvedInns.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {medication.resolvedInns.map((inn) => (
              <Badge key={inn} variant="secondary" className="font-normal">
                {inn}
              </Badge>
            ))}
          </div>
        )}
        {(medication.dosage || medication.startedAt || medication.stoppedAt) && (
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            {medication.dosage && <span>{medication.dosage}</span>}
            {medication.startedAt && (
              <span>{t("started")}: {medication.startedAt}</span>
            )}
            {medication.stoppedAt && (
              <span>{t("stopped")}: {medication.stoppedAt}</span>
            )}
          </div>
        )}
        {medication.notes && (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {medication.notes}
          </p>
        )}
        <div className="flex justify-end gap-1 pt-1">
          <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
            {tc("edit")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            {tc("delete")}
          </Button>
        </div>
      </div>
      <EditDialog
        medication={medication}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      <DeleteDialog
        medication={medication}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export default function MedicationsPage() {
  const t = useTranslations("medications");
  const onBehalfOfUserId = useDelegationStore((s) => s.onBehalfOfUserId);
  const { data: medications, isLoading } = useMedications();

  const grouped = useMemo(() => {
    if (!medications) return [];
    return STATUS_ORDER.map((status) => ({
      status,
      ...STATUS_BADGE[status],
      items: medications.filter((m) => m.status === status),
    })).filter((g) => g.items.length > 0);
  }, [medications]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold tracking-tight">
        {t(onBehalfOfUserId ? "title" : "myTitle")}
      </h1>

      <AddMedicationForm />

      {grouped.length > 0 ? (
        <div className="space-y-6">
          {grouped.map((group) => (
            <section key={group.status}>
              <h2 className="mb-3 text-sm font-medium text-muted-foreground">
                {t(group.key)} ({group.items.length})
              </h2>
              <div className="space-y-3">
                {group.items.map((m) => (
                  <MedicationRow key={m.id} medication={m} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {t("emptyState")}
          </p>
        </div>
      )}
    </div>
  );
}
